import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';

import { ConnectionService } from '../services/connection.service';
import { UserService } from '../services/user.service';
import { Stylist } from '../models/stylist/stylist.model';

/**
 * Guards the Connections surface on the per-stylist connect-accounts
 * permission — the authorisation layer.
 *
 * It sits AFTER FeatureFlagGuard on the route, and the two are deliberately
 * separate: PostHog's `connectable-accounts` flag answers "is this rolled out
 * to this salon", this answers "may this stylist take part". Both fail closed
 * independently, so neither can be the single point that opens the feature
 * (see docs/solutions/architecture-patterns/posthog-feature-flag-fail-closed-gating.md).
 *
 * Fails CLOSED, unlike `FeatureFlagGuard`. That difference is the point.
 * FeatureFlagGuard protects a rollout, where a misconfiguration should not
 * strand a user, so it admits when it cannot decide. This guards a capability
 * that shares one stylist's calendar and client list with a stylist in ANOTHER
 * salon, so a state we cannot resolve — no user yet, a stylist record that has
 * not rehydrated, a user object that predates the accessor — denies.
 *
 * Denial is not one experience, because it lands on two different people:
 *
 *  - A stylist who never had connections is redirected silently. The feature
 *    is simply not theirs and there is nothing to explain.
 *  - A stylist who HOLDS connections but has lost the permission gets an
 *    explanation naming their administrator as the way to get it back. For
 *    them a surface they were using has vanished, and a silent bounce to
 *    /bookings reads as a bug, not as a policy.
 *
 * Which of the two is chosen is decided by a /connections lookup, and that
 * lookup is only ever allowed to pick between two DENIALS — never to grant
 * access, and never to assert revocation it could not confirm. An empty,
 * failed, or unusable response falls back to the silent redirect.
 */
@Injectable({
  providedIn: 'root',
})
export class ConnectAccountsAccessGuard implements CanActivate {
  constructor(
    private userService: UserService,
    private connectionService: ConnectionService,
    private alertCtrl: AlertController,
    private router: Router
  ) {}

  public async canActivate(): Promise<boolean | UrlTree> {
    const user: unknown = this.userService.getUser();

    // `getCanConnectAccountsEffective()` is the one thing to gate on: the
    // server's `administrator OR canConnectAccounts`, so an administrator
    // holds the capability without the column being set and a restored
    // session still resolves. The typeof check keeps a partially-restored or
    // stale user object from throwing its way into an implicit allow.
    const permitted = Stylist.mayConnectAccounts(user);

    if (permitted) {
      return true;
    }

    const denied = this.router.parseUrl('/bookings');

    // Nobody to explain anything to, and no identity to look connections up
    // for. Straight out.
    if (!user) {
      return denied;
    }

    if (await this.holdsConnections()) {
      await this.explainLostAccess();
    }

    return denied;
  }

  /**
   * Whether this stylist actually has live connections — the only thing that
   * upgrades a silent redirect to an explanation.
   *
   * Deliberately collapses "no connections", "lookup failed" and "lookup
   * returned something we cannot read" into the same `false`.
   *
   * Costs one GET, and only on a denial — a permitted stylist never reaches
   * here. On a dead network the shared read retries twice with a 1s delay, so
   * the redirect can lag a couple of seconds before falling through to the
   * silent branch; that is the price of not bouncing a stylist who was using
   * the feature yesterday without a word.
   *
   * The caller has
   * already denied by this point, so this cannot become a fail-open path; the
   * conservative answer here just means the quieter of two denials.
   */
  private async holdsConnections(): Promise<boolean> {
    try {
      const connections = await firstValueFrom(this.connectionService.getConnections());
      return Array.isArray(connections) && connections.length > 0;
    } catch (e) {
      // Collapses to the quieter denial, but say so — otherwise a stylist who
      // really does hold connections gets the silent redirect and there is
      // nothing in the console explaining why they were not told.
      console.error('[ConnectAccountsAccessGuard] connections lookup failed', e);
      return false;
    }
  }

  private async explainLostAccess(): Promise<void> {
    try {
      const alert = await this.alertCtrl.create({
        header: 'Connections are no longer available to you',
        message:
          'Your existing connections are still in place, but permission to manage ' +
          'them has been turned off for your account. Ask your salon administrator ' +
          'to switch "Connect accounts" back on for you under Settings → Staff.',
        buttons: ['OK'],
      });
      await alert.present();
    } catch (e) {
      // An alert that fails to present must not turn a denial into an error
      // the router surfaces as a failed navigation.
      console.error('[ConnectAccountsAccessGuard] could not present the access notice', e);
    }
  }
}
